/**
 * POS · Sessions — cash-drawer open / count / close + cash movements
 * (drop / pay-in / payout). Office+ server-side. FUNCTIONAL scaffold for the
 * design pass: plain AntD, real API calls, variance shown on close.
 */

import { useCallback, useEffect, useState } from "react";

import {
  App as AntApp,
  Button,
  Card,
  Descriptions,
  Empty,
  InputNumber,
  Segmented,
  Select,
  Space,
  Typography,
} from "antd";

import {
  ApiError,
  api,
  type PosSession,
  type PosStation,
} from "../../api/client";

const eur = (c: number | null | undefined) =>
  c == null ? "—" : `€${(c / 100).toFixed(2)}`;

export const PosSessionsPage = () => {
  const { message } = AntApp.useApp();
  const [stations, setStations] = useState<PosStation[]>([]);
  const [posId, setPosId] = useState<number | null>(null);
  const [session, setSession] = useState<PosSession | null>(null);
  const [busy, setBusy] = useState(false);

  const [floatEur, setFloatEur] = useState<number | null>(null);
  const [countEur, setCountEur] = useState<number | null>(null);
  const [moveKind, setMoveKind] = useState<"drop" | "pay_in" | "payout">("drop");
  const [moveEur, setMoveEur] = useState<number | null>(null);

  useEffect(() => {
    void api.posListStations().then((st) => {
      setStations(st);
      if (st.length) setPosId(st[0].id);
    });
  }, []);

  const refresh = useCallback(async () => {
    if (posId == null) return;
    try {
      setSession(await api.posCurrentSession(posId));
    } catch (e) {
      message.error(e instanceof ApiError ? e.detail : "Failed to load session");
    }
  }, [posId, message]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const guard = useCallback(
    async (fn: () => Promise<void>) => {
      setBusy(true);
      try {
        await fn();
        await refresh();
      } catch (e) {
        message.error(e instanceof ApiError ? e.detail : "Action failed");
      } finally {
        setBusy(false);
      }
    },
    [refresh, message],
  );

  const open = () =>
    guard(async () => {
      if (posId == null) return;
      await api.posOpenSession({
        pos_id: posId,
        opening_float_cents: Math.round((floatEur ?? 0) * 100),
      });
      setFloatEur(null);
      message.success("Session opened");
    });

  const addMovement = () =>
    guard(async () => {
      if (!session || moveEur == null) return;
      await api.posAddCashMovement(session.id, {
        kind: moveKind,
        amount_cents: Math.round(moveEur * 100),
      });
      setMoveEur(null);
    });

  const close = () =>
    guard(async () => {
      if (!session || countEur == null) return;
      const r = await api.posCloseSession(session.id, {
        counted_cents: Math.round(countEur * 100),
      });
      setCountEur(null);
      message.info(`Closed — variance ${eur(r.variance_cents)}`);
    });

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Typography.Title level={3}>Cash-drawer sessions</Typography.Title>

      <Select
        style={{ width: 240 }}
        value={posId ?? undefined}
        onChange={setPosId}
        options={stations.map((s) => ({ value: s.id, label: s.name }))}
        placeholder="Station"
      />

      {session === null ? (
        <Card title="Drawer closed">
          <Space>
            <InputNumber
              prefix="€"
              placeholder="Opening float"
              min={0}
              value={floatEur}
              onChange={setFloatEur}
            />
            <Button type="primary" loading={busy} onClick={open} disabled={posId == null}>
              Open session
            </Button>
          </Space>
          {posId == null && <Empty description="No stations" />}
        </Card>
      ) : (
        <>
          <Card title={`Open session #${session.id}`}>
            <Descriptions column={2} size="small">
              <Descriptions.Item label="Opening float">
                {eur(session.opening_float_cents)}
              </Descriptions.Item>
              <Descriptions.Item label="Expected drawer">
                {eur(session.expected_cents)}
              </Descriptions.Item>
              <Descriptions.Item label="Comps">
                {session.comp_count} ({eur(session.comp_total_cents)})
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title="Cash movement">
            <Space>
              <Segmented
                value={moveKind}
                onChange={(v) => setMoveKind(v as typeof moveKind)}
                options={[
                  { value: "drop", label: "Drop" },
                  { value: "pay_in", label: "Pay-in" },
                  { value: "payout", label: "Payout" },
                ]}
              />
              <InputNumber
                prefix="€"
                placeholder="Amount"
                min={0}
                value={moveEur}
                onChange={setMoveEur}
              />
              <Button loading={busy} onClick={addMovement} disabled={moveEur == null}>
                Record
              </Button>
            </Space>
          </Card>

          <Card title="Close drawer">
            <Space>
              <InputNumber
                prefix="€"
                placeholder="Counted cash"
                min={0}
                value={countEur}
                onChange={setCountEur}
              />
              <Button danger loading={busy} onClick={close} disabled={countEur == null}>
                Count &amp; close
              </Button>
            </Space>
          </Card>
        </>
      )}
    </Space>
  );
};
